import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export default function BusinessCoachWidget() {
  const [snapshot, setSnapshot] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | empty | stale | error
  const nav = useNavigate();

  useEffect(() => {
    let stop = false;
    api.get("/business-coach/snapshot?period=this_week")
      .then(({ data }) => {
        if (stop) return;
        setSnapshot(data);
        if (!data?.priorities?.length) setStatus("empty");
        else {
          // Stale = generated_at > 10 min ago (defensive; ctx cache is 3 min)
          const gen = data?.period?.generated_at;
          const ageMin = gen ? (Date.now() - new Date(gen).getTime()) / 60000 : 0;
          setStatus(ageMin > 10 ? "stale" : "ready");
        }
      })
      .catch(() => !stop && setStatus("error"));
    return () => { stop = true; };
  }, []);

  const top = snapshot?.priorities?.[0];
  const sources = snapshot?.sources_summary;

  return (
    <Card
      className="p-5 mb-6 border-orange-100 bg-gradient-to-br from-orange-50/50 to-white cursor-pointer hover:shadow-md transition"
      onClick={() => nav("/app/coach")}
      data-testid="business-coach-widget"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">
            <Sparkles className="w-3 h-3"/> Today's #1 priority
            {status === "stale" && <span className="text-[10px] text-zinc-400 normal-case">· cached</span>}
          </div>

          {status === "loading" && (
            <div className="flex items-center gap-2 text-sm text-zinc-500" data-testid="business-coach-widget-loading">
              <Loader2 className="w-3 h-3 animate-spin"/> Coach is looking at your numbers…
            </div>
          )}
          {status === "error" && (
            <div className="text-sm text-rose-600" data-testid="business-coach-widget-error">
              Couldn't load Coach signals. Try opening the Coach page.
            </div>
          )}
          {status === "empty" && (
            <div data-testid="business-coach-widget-empty">
              <div className="font-heading text-lg font-bold">Nothing urgent right now</div>
              <div className="text-sm text-zinc-500 mt-1">
                No stale estimates, overdue invoices, or capacity gaps demanding attention today.
              </div>
            </div>
          )}
          {(status === "ready" || status === "stale") && top && (
            <div data-testid="business-coach-widget-priority">
              <div className="font-heading text-lg font-bold">{top.reason}</div>
              <div className="text-sm text-zinc-600 mt-1">
                {top.potential_value != null && (
                  <span>Value at stake: <b>${Number(top.potential_value).toLocaleString(undefined, {maximumFractionDigits: 0})}</b> · </span>
                )}
                {top.type === "follow_up_estimate" && "Follow up on this estimate today"}
                {top.type === "follow_up_lead" && "Reach out to this lead today"}
                {top.type === "collect_overdue" && "Collect this overdue balance today"}
                {top.type === "schedule_gap" && "Fill upcoming schedule capacity"}
                {top.type === "revenue_pace" && "Behind pace — the fastest recovery paths are on the Coach page"}
              </div>
              {sources && (
                <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-mono-accent mt-2">
                  Based on {sources.leads_analyzed} leads · {sources.estimates_analyzed} estimates · {sources.invoices_analyzed} invoices
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <Button
            onClick={(e) => { e.stopPropagation(); nav("/app/coach"); }}
            className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
            data-testid="business-coach-widget-cta"
          >
            Open Coach <ArrowRight className="w-4 h-4 ml-1"/>
          </Button>
          {top?.action_url && (
            <a
              href={top.action_url}
              onClick={(e) => e.stopPropagation()}
              className="text-xs text-[#FF5A00] hover:underline text-center"
              data-testid="business-coach-widget-action"
            >Open record →</a>
          )}
        </div>
      </div>
    </Card>
  );
}
